import { useState, useEffect, useCallback, useRef } from "react";
import {
  FilterBar,
  Field,
  Select,
  SearchInput,
  Spinner,
  EmptyState,
  IconButton,
} from "../components/FioriUI";
import { config } from "../config";

// ─── Endpoints ────────────────────────────────────────────────────────────────
const CASH_APP_API_URL = "/api/cash-application/orders";

const COLORS = {
  navy: "#131a33",
  blue: "#2447b0",
  blueDark: "#1d3fa8",
  blueSoft: "#e8eefb",
  blueText: "#1e3a8a",
  ink: "#111827",
  muted: "#6b7280",
  line: "#e5e7eb",
  page: "#f7f8fa",
  green: "#15803d",
  greenBg: "#eaf7ee",
  amber: "#a16207",
  amberBg: "#fdf4e3",
  red: "#b91c1c",
  redBg: "#fdecec",
};

// ─── SOP Data ─────────────────────────────────────────────────────────────────
const SOPS = {
  cashapp: {
    title: "CA-OPS · Cash Application Scenario Dispatch",
    sub: "Cash Application Exception Standard · v1.0",
    rows: [
      { s: "CA_EXACT_MATCH", c: "Payment matches invoice amount exactly", d: "AUTO_APPLY", t: "—" },
      { s: "CA_WITHIN_TOLERANCE", c: "Payment variance ≤ 2%, no dispute flags", d: "AUTO_APPLY", t: "—" },
      { s: "CA_MINOR_VARIANCE", c: "Payment variance 2–5%, clean customer history", d: "ESCALATE", t: "T2" },
      { s: "CA_MAJOR_VARIANCE", c: "Payment variance > 5%, or multiple invoice references", d: "HOLD", t: "T3" },
      { s: "CA_DISPUTED_PAYMENT", c: "Dispute flag raised, or chargeback history", d: "HOLD", t: "T3" },
      { s: "CA_UNAPPLIED_CASH", c: "No invoice reference, or unidentified remittance", d: "ESCALATE", t: "T4" },
      { s: "CA_OVER_PAYMENT", c: "Payment exceeds invoice total by > 10%", d: "HOLD", t: "T3" },
    ],
  },
};

// ─── Shared UI Components ─────────────────────────────────────────────────────
function Pill({ tone, children }) {
  const map = {
    g: { bg: COLORS.greenBg, c: COLORS.green },
    a: { bg: COLORS.amberBg, c: COLORS.amber },
    r: { bg: COLORS.redBg, c: COLORS.red },
    b: { bg: COLORS.blueSoft, c: COLORS.blueText },
  };
  const s = map[tone] || map.b;
  return (
    <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 600, padding: "4px 9px", borderRadius: 5, whiteSpace: "nowrap", background: s.bg, color: s.c }}>
      {children}
    </span>
  );
}

function SopChip({ label, onClick }) {
  return (
    <span onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#eef2ff", border: "1px solid #c7d2fe", color: "#3730a3", borderRadius: 5, padding: "3px 8px", fontSize: 10.5, fontWeight: 600, cursor: "pointer" }}>
      {label}
    </span>
  );
}

function Kpi({ label, value, sub, tone }) {
  const toneColor = tone === "g" ? COLORS.green : tone === "a" ? COLORS.amber : tone === "r" ? COLORS.red : COLORS.ink;
  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.line}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 27, fontWeight: 600, marginTop: 5, lineHeight: 1.1, color: toneColor }}>{value}</div>
      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function Row({ l, r, rColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #f1f3f5", fontSize: 13 }}>
      <span style={{ color: COLORS.muted }}>{l}</span>
      <span style={{ textAlign: "right", fontWeight: 500, color: rColor || COLORS.ink }}>{r}</span>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.line}`, borderRadius: 9, overflow: "hidden", marginBottom: 14 }}>
      <h4 style={{ margin: 0, background: "#3560c9", color: "#fff", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.6, padding: "9px 13px", fontWeight: 600 }}>
        {title}
      </h4>
      <div style={{ padding: 13 }}>{children}</div>
    </div>
  );
}

function Step({ n, status, desc, agent, tone, time, isLast, summary, category, webhook_id }) {
  // Format time consistently
  const formatTime = (timestamp) => {
    if (!timestamp) return null;
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC' });
    } catch (e) {
      return null;
    }
  };
  
  const formattedTime = formatTime(time);
  
  // Simple dot colors based on status
  const dotColor = tone === "r" || status === 'FAILED' ? "#BB0000" : tone === "s" || status === 'SKIPPED' ? "#9ca3af" : "#0070F2";
  
  return (
    <div style={{ display: "flex", gap: 10, position: "relative", paddingBottom: isLast ? 0 : 13 }}>
      {/* Time column */}
      <div style={{ width: 56, flexShrink: 0, textAlign: "right", fontFamily: "Consolas, Menlo, monospace", fontSize: 11, color: "#556B82", paddingTop: 3 }}>
        {formattedTime || ""}
      </div>
      
      {/* Dot and vertical line column */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative", width: 8, flexShrink: 0 }}>
        {/* Simple dot */}
        <span style={{ 
          position: 'absolute',
          left: 0,
          top: 6,
          width: 8,
          height: 8,
          borderRadius: "50%", 
          background: dotColor,
          zIndex: 2
        }} />
        
        {/* Vertical connecting line */}
        {!isLast && (
          <span style={{ 
            position: 'absolute',
            left: 3.5,
            top: 14,
            bottom: -13,
            width: 1,
            background: "#D9D9D9"
          }} />
        )}
      </div>
      
      {/* Content column */}
      <div style={{ flex: 1, minWidth: 0, paddingLeft: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: "#1D2D3E", lineHeight: 1.5, marginBottom: 1 }}>
          {desc}
        </div>
        {agent && (
          <div style={{ fontSize: 12, color: "#556B82", marginTop: 1 }}>
            {agent}
          </div>
        )}
        {webhook_id && (
          <div style={{ fontSize: 10.5, color: "#A9B4BE", marginTop: 1, fontFamily: "Consolas, Menlo, monospace" }}>
            webhook {webhook_id}
          </div>
        )}
        {category && category !== desc && (
          <div style={{ fontSize: 11, color: "#A9B4BE", marginTop: 2, fontFamily: "Consolas, Menlo, monospace", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {category}
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{k}</div>
      <div style={{ fontSize: 13.5, marginTop: 2 }}>{v}</div>
    </div>
  );
}

function SopTable({ rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${COLORS.line}`, fontSize: 12 }}>
      <thead>
        <tr>
          {["Scenario", "Condition", "Decision", "Tier"].map(h => (
            <th key={h} style={{ background: "#f8fafc", textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${COLORS.line}`, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.muted }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.s} style={r.match ? { background: "#fef9e7", fontWeight: 500 } : {}}>
            <td style={{ padding: "7px 10px", borderBottom: `1px solid ${COLORS.line}`, fontSize: 11 }}>{r.s}</td>
            <td style={{ padding: "7px 10px", borderBottom: `1px solid ${COLORS.line}` }}>{r.c}</td>
            <td style={{ padding: "7px 10px", borderBottom: `1px solid ${COLORS.line}`, fontWeight: 600 }}>{r.d}</td>
            <td style={{ padding: "7px 10px", borderBottom: `1px solid ${COLORS.line}` }}>{r.t}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const btnStyle = {
  padding: "7px 13px", borderRadius: 6, border: `1px solid ${COLORS.line}`,
  background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: COLORS.ink, transition: "all 0.15s",
};
const dcloseStyle = { background: "rgba(255,255,255,.15)", border: "none", color: "#fff", fontSize: 22, width: 32, height: 32, borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };

const tdStyle = (idx, total) => ({
  padding: "11px 14px", borderBottom: idx === total - 1 ? "none" : `1px solid ${COLORS.line}`, fontSize: 13, verticalAlign: "top",
});

// Tab button component
function TabButton({ active, onClick, children, count }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 12,
        borderRadius: 4,
        padding: "4px 11px",
        cursor: "pointer",
        border: `1px solid ${active ? COLORS.blue : COLORS.line}`,
        background: active ? COLORS.blue : "#fff",
        color: active ? "#fff" : COLORS.muted,
        fontWeight: active ? 500 : 400,
        transition: "all 0.15s",
      }}
    >
      {children}{count !== undefined ? ` ${count}` : ''}
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CashApplication() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [sidebarError, setSidebarError] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [sopKey, setSopKey] = useState(null);
  const [expandedActions, setExpandedActions] = useState({});
  const [filters, setFilters] = useState({ status: "", exception_type: "", search: "" });

  const [exceptionTypes, setExceptionTypes] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [stats, setStats] = useState({
    total_orders: 0, auto_released: 0, escalated: 0, on_hold: 0,
    unique_branches: 0, auto_release_rate: 0, avg_time_to_release_minutes: 0, avg_exposure_pct: 0,
  });

  // Polling state
  const [isPolling, setIsPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const pollingIntervalRef = useRef(null);
  const initialStatusRef = useRef(null);

  const anyOpen = drawerOpen || sopKey;
  const openSop = () => setSopKey("cashapp");
  const closeSop = () => setSopKey(null);
  const closeAll = () => { setDrawerOpen(false); setSopKey(null); };

  const toggleActions = (id) => setExpandedActions((p) => ({ ...p, [id]: !p[id] }));

  // Exception Form State
  const [formData, setFormData] = useState({
    selectedOption: null,
    decision: null,
    customerSearch: '',
    selectedCustomer: null,
    addMapping: false,
    notes: ''
  });

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async (action) => {
    try {
      // Validate we have required data
      if (!selectedOrder || !selectedOrder.run_id) {
        throw new Error('Missing order or run_id');
      }

      // Clean up form_data - only include non-null, non-empty values
      const cleanedFormData = Object.entries(formData).reduce((acc, [key, value]) => {
        // Include if: not null, not undefined, not empty string (except for notes which can be empty)
        if (value !== null && value !== undefined && (value !== '' || key === 'notes')) {
          acc[key] = value;
        }
        return acc;
      }, {});

      // Step 1: Save to database first
      const resolveUrl = `${CASH_APP_API_URL}/${selectedOrder.run_id}/resolve`;
      
      const payload = {
        run_id: selectedOrder.run_id,
        action: action, // 'submit' or 'hold'
        bank_txn_id: selectedOrder.bank_txn_id, // Add bank_txn_id at root level
        form_data: cleanedFormData, // User's form selections
        exception_type: selectedOrder.exception_code || selectedOrder.exception_name,
        exception_name: selectedOrder.exception_name,
        // Include full order data for context (this is the form content displayed to user)
        order_data: {
          bank_txn_id: selectedOrder.bank_txn_id,
          receipt_id: selectedOrder.receipt_id,
          customer_id: selectedOrder.customer_id,
          customer_name: selectedOrder.customer_name,
          receipt_amount: selectedOrder.receipt_amount,
          currency: selectedOrder.currency,
          value_date: selectedOrder.value_date,
          ach_trace_number: selectedOrder.ach_trace_number,
          receipt_status: selectedOrder.receipt_status,
          company_code: selectedOrder.company_code,
          route: selectedOrder.route,
        },
        timestamp: new Date().toISOString()
      };
      
      console.log('💾 Saving to database:', { action, resolveUrl, run_id: selectedOrder.run_id, cleaned_form_data: cleanedFormData, bank_txn_id: selectedOrder.bank_txn_id });
      
      // Save to database
      const dbResponse = await fetch(resolveUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      // Check content type before parsing
      const contentType = dbResponse.headers.get('content-type');
      console.log('📥 Database response:', { status: dbResponse.status, contentType });
      
      if (!dbResponse.ok) {
        let errorMessage = `HTTP ${dbResponse.status}`;
        if (contentType && contentType.includes('application/json')) {
          const errorJson = await dbResponse.json();
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } else {
          const errorText = await dbResponse.text();
          errorMessage = errorText.substring(0, 200); // First 200 chars
        }
        throw new Error(`Failed to save to database: ${errorMessage}`);
      }
      
      const dbResult = await dbResponse.json();
      console.log('✅ Saved to database:', dbResult);
      
      // Step 2: Call webhook with specific format expected by Karini
      const webhookUrl = `/api/webhook/recipe/${config.cashApplication.webhookRecipeId}`;
      
      // Get bank_txn_id from database response or selectedOrder
      const bankTxnIdForWebhook = dbResult.bank_txn_id || selectedOrder.bank_txn_id;
      
      // Webhook expects this specific format with key: value in input_message
      const webhookPayload = {
        files: [],
        input_message: `bank_txn_id: ${bankTxnIdForWebhook}`, // Include key with value
        bucket: "",
        object_key_path: "",
        max_files: 10
      };
      
      console.log('📤 Submitting to webhook:', { action, webhookUrl, input_message: webhookPayload.input_message });
      
      const webhookResponse = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-api-token': config.cashApplication.apiToken,
        },
        body: JSON.stringify(webhookPayload)
      });
      
      if (!webhookResponse.ok) {
        const errorText = await webhookResponse.text();
        console.warn('⚠️ Webhook failed, but data is saved:', errorText);
        // Don't throw - data is already saved to database
      } else {
        const webhookResult = await webhookResponse.json();
        console.log('✅ Webhook response:', webhookResult);
        
        // Start polling for agent updates
        startPolling(selectedOrder.run_id);
      }
      
      alert(`Form ${action}ed successfully. Agent is processing...`);
      
      // Close the sidebar after successful submission
      setDrawerOpen(false);
      setSelectedOrder(null);
      setActiveTab('details'); // Reset to details tab
      
      fetchOrders(); // Refresh the list
    } catch (err) {
      console.error('❌ Error submitting form:', err);
      alert(`Failed to ${action} form: ${err.message}`);
    }
  };

  const renderExceptionForm = (order) => {
    if (!order) return null;
    
    // Support both nested agent6_output and spread structure
    // If agent6_output exists as a property, use it; otherwise use the order itself
    const agent6 = order.agent6_output || order;
    
    // Check if we have any exception data at all
    const hasExceptionData = order.exception_code || order.exception_name || 
                              agent6.exception_code || agent6.exception_name;
    
    // Debug logging
    console.log('🔍 Render Exception Form Debug:', {
      hasAgent6Output: !!order.agent6_output,
      exceptionCode: order.exception_code || agent6.exception_code,
      exceptionName: order.exception_name || agent6.exception_name,
      hasExceptionData,
      orderKeys: Object.keys(order).slice(0, 20) // First 20 keys
    });
    
    if (!hasExceptionData) {
      console.log('⚠️ No exception data found, not rendering form');
      return null;
    }

    const exceptionCode = order.exception_code || agent6.exception_code || '';
    const exceptionName = order.exception_name || agent6.exception_name || '';

    // SC-05 — Ambiguous Match (EX-201)
    if (exceptionCode === 'EX-201' || exceptionName.includes('AMBIGUOUS')) {
      const candidateSets = agent6.hitl_packet?.candidate_sets || [];
      
      return (
        <Card title="⚠️ AMBIGUOUS MATCH — Resolution Form">
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Payment:</strong> ${parseFloat(order.receipt_amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})} {order.currency || 'USD'}
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>From:</strong> {order.customer_name || 'N/A'}
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Trace:</strong> {order.ach_trace_number || 'N/A'}
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Invoice:</strong> {agent6.source_document?.channel || 'none — remittance had no line item detail'}
            </div>
          </div>

          <div style={{ padding: '10px', background: COLORS.amberBg, borderRadius: 6, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.amber, marginBottom: 8 }}>
              Problem: {candidateSets.length || 4} valid combinations found
            </div>
            {candidateSets.length > 0 ? candidateSets.map((set, idx) => (
              <label key={idx} style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
                <input
                  type="radio"
                  name="ambiguous_choice"
                  value={idx}
                  checked={formData.selectedOption === idx}
                  onChange={() => handleFormChange('selectedOption', idx)}
                  style={{ marginRight: 8 }}
                />
                <span style={{ fontSize: 12 }}>{set.description || set.invoice_numbers?.join(' + ') || `Option ${idx + 1}`} (${set.total_amount || 'N/A'})</span>
              </label>
            )) : (
              <>
                <label style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
                  <input type="radio" name="ambiguous_choice" value={0} checked={formData.selectedOption === 0} onChange={() => handleFormChange('selectedOption', 0)} style={{ marginRight: 8 }} />
                  <span style={{ fontSize: 12 }}>WK-2026-0091700 ($12,500.00 exact)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
                  <input type="radio" name="ambiguous_choice" value={1} checked={formData.selectedOption === 1} onChange={() => handleFormChange('selectedOption', 1)} style={{ marginRight: 8 }} />
                  <span style={{ fontSize: 12 }}>WK-2026-0091688 + WK-2026-0091689</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
                  <input type="radio" name="ambiguous_choice" value={2} checked={formData.selectedOption === 2} onChange={() => handleFormChange('selectedOption', 2)} style={{ marginRight: 8 }} />
                  <span style={{ fontSize: 12 }}>WK-2026-0091688 + WK-2026-0092440</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
                  <input type="radio" name="ambiguous_choice" value={3} checked={formData.selectedOption === 3} onChange={() => handleFormChange('selectedOption', 3)} style={{ marginRight: 8 }} />
                  <span style={{ fontSize: 12 }}>WK-2026-0091689 + WK-2026-0092440</span>
                </label>
              </>
            )}
          </div>

          {agent6.matching_evidence?.tolerance_note && (
            <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 12, padding: '8px', background: COLORS.blueSoft, borderRadius: 4 }}>
              <strong>Note:</strong> {agent6.matching_evidence.tolerance_note}
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
              Notes:
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              placeholder="Add resolution notes..."
              style={{ width: '100%', minHeight: '60px', padding: '8px', borderRadius: 6, border: `1px solid ${COLORS.line}`, fontSize: 13, resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleFormSubmit('submit')} style={{ ...btnStyle, background: COLORS.green, borderColor: COLORS.green, color: '#fff', flex: 1 }}>
              SUBMIT
            </button>
            <button onClick={() => handleFormSubmit('hold')} style={{ ...btnStyle, background: COLORS.amber, borderColor: COLORS.amber, color: '#fff', flex: 1 }}>
              HOLD
            </button>
          </div>
        </Card>
      );
    }

    // SC-26 — Seat-Count Dispute / Short Pay (EX-202)
    if (exceptionCode === 'EX-202' || exceptionName.includes('SHORT PAY') || exceptionName.includes('DISPUTE')) {
      const applications = agent6.applications || order.applications || [];
      const variance = agent6.variance || order.matching_evidence?.variance || 0;
      const varianceDirection = agent6.variance_direction || order.matching_evidence?.variance_direction || 'NONE';
      const matchSetTotal = agent6.matching_evidence?.match_set_total || 0;

      return (
        <Card title="🔴 SHORT PAY — DISPUTE Resolution Form">
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Payment:</strong> ${parseFloat(order.receipt_amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>From:</strong> {order.customer_name || 'N/A'}
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Match set total:</strong> ${parseFloat(matchSetTotal).toLocaleString('en-US', {minimumFractionDigits: 2})}
            </div>
            <div style={{ fontSize: 13, marginBottom: 8, color: COLORS.red, fontWeight: 600 }}>
              <strong>Variance:</strong> ${parseFloat(variance).toLocaleString('en-US', {minimumFractionDigits: 2})} ({varianceDirection})
            </div>
          </div>

          {applications.length > 0 && (
            <div style={{ marginBottom: 14, padding: '10px', background: '#f9fafb', borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', color: COLORS.muted }}>Invoice Applications:</div>
              {applications.map((app, idx) => (
                <div key={idx} style={{ fontSize: 12, marginBottom: 4, paddingBottom: 4, borderBottom: idx < applications.length - 1 ? `1px solid ${COLORS.line}` : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 600 }}>{app.invoice_number}</span>
                    <span>${parseFloat(app.amount_applied || app.open_amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                  </div>
                  {app.note || app.deduction_note ? (
                    <div style={{ fontSize: 11, color: COLORS.blueText, marginTop: 2, fontStyle: 'italic' }}>
                      {app.note || app.deduction_note}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          {agent6.deduction_note && (
            <div style={{ fontSize: 12, marginBottom: 12, padding: '8px', background: COLORS.blueSoft, borderRadius: 4, color: COLORS.blueText }}>
              <strong>Payer claims:</strong> {agent6.deduction_note}
            </div>
          )}

          <div style={{ marginBottom: 14, padding: '10px', background: COLORS.amberBg, borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Decision:</div>
            <label style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
              <input
                type="radio"
                name="dispute_decision"
                value="customer_right"
                checked={formData.decision === 'customer_right'}
                onChange={() => handleFormChange('decision', 'customer_right')}
                style={{ marginRight: 8, marginTop: 2 }}
              />
              <span style={{ fontSize: 12 }}>Customer right → apply Z21, credit line</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
              <input
                type="radio"
                name="dispute_decision"
                value="we_are_right"
                checked={formData.decision === 'we_are_right'}
                onChange={() => handleFormChange('decision', 'we_are_right')}
                style={{ marginRight: 8, marginTop: 2 }}
              />
              <span style={{ fontSize: 12 }}>We are right → collect ${Math.abs(parseFloat(variance)).toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
              <input
                type="radio"
                name="dispute_decision"
                value="need_more_info"
                checked={formData.decision === 'need_more_info'}
                onChange={() => handleFormChange('decision', 'need_more_info')}
                style={{ marginRight: 8, marginTop: 2 }}
              />
              <span style={{ fontSize: 12 }}>Need more info → hold, request contract</span>
            </label>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
              Route: <span style={{ fontWeight: 700, color: COLORS.amber }}>DEDUCTIONS</span>
            </label>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
              Notes:
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              placeholder="Add resolution notes..."
              style={{ width: '100%', minHeight: '60px', padding: '8px', borderRadius: 6, border: `1px solid ${COLORS.line}`, fontSize: 13, resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleFormSubmit('submit')} style={{ ...btnStyle, background: COLORS.green, borderColor: COLORS.green, color: '#fff', flex: 1 }}>
              SUBMIT
            </button>
            <button onClick={() => handleFormSubmit('hold')} style={{ ...btnStyle, background: COLORS.amber, borderColor: COLORS.amber, color: '#fff', flex: 1 }}>
              HOLD
            </button>
          </div>
        </Card>
      );
    }

    // SC-09 — Duplicate Payment (EX-105)
    if (exceptionCode === 'EX-105' || exceptionName.includes('DUPLICATE')) {
      const remittanceLines = agent6.remittance_lines || order.remittance_lines || [];
      const invoice = remittanceLines[0]?.ref_value_canonical || 'N/A';

      return (
        <Card title="🔁 DUPLICATE PAYMENT Resolution Form">
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Customer:</strong> {order.customer_name || 'N/A'}
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Invoice:</strong> {invoice}
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Amount:</strong> ${parseFloat(order.receipt_amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
            </div>
          </div>

          <div style={{ marginBottom: 14, padding: '10px', background: '#f9fafb', borderRadius: 6, border: `1px solid ${COLORS.line}` }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>This receipt:</div>
            <div style={{ fontSize: 12, marginBottom: 3 }}>bank_txn_id: {order.bank_txn_id}</div>
            <div style={{ fontSize: 12, marginBottom: 3 }}>value_date: {order.value_date}</div>
            <div style={{ fontSize: 12 }}>trace: {order.ach_trace_number}</div>
          </div>

          {agent6.matching_evidence?.tolerance_note && (
            <div style={{ marginBottom: 14, padding: '10px', background: COLORS.amberBg, borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: COLORS.amber }}>Flagged against prior run:</div>
              <div style={{ fontSize: 12, color: COLORS.ink }}>{agent6.matching_evidence.tolerance_note}</div>
            </div>
          )}

          <div style={{ marginBottom: 14, padding: '10px', background: COLORS.redBg, borderRadius: 6, border: '1px solid #fecaca' }}>
            <div style={{ fontSize: 12, color: COLORS.red }}>
              ⚠ Prior run posted AFTER this receipt's value date — verify this is a genuine duplicate and not a date entry error
            </div>
          </div>

          <div style={{ marginBottom: 14, padding: '10px', background: COLORS.blueSoft, borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Your Action:</div>
            <label style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
              <input
                type="radio"
                name="duplicate_action"
                value="refund"
                checked={formData.decision === 'refund'}
                onChange={() => handleFormChange('decision', 'refund')}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontSize: 12 }}>Refund to customer</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
              <input
                type="radio"
                name="duplicate_action"
                value="credit"
                checked={formData.decision === 'credit'}
                onChange={() => handleFormChange('decision', 'credit')}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontSize: 12 }}>Hold as credit on account</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
              <input
                type="radio"
                name="duplicate_action"
                value="manual_review"
                checked={formData.decision === 'manual_review'}
                onChange={() => handleFormChange('decision', 'manual_review')}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontSize: 12 }}>Manual review — dates need checking</span>
            </label>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
              Route: <span style={{ fontWeight: 700, color: COLORS.blue }}>AR_ANALYST</span>
            </label>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
              Notes:
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              placeholder="Add resolution notes..."
              style={{ width: '100%', minHeight: '60px', padding: '8px', borderRadius: 6, border: `1px solid ${COLORS.line}`, fontSize: 13, resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleFormSubmit('submit')} style={{ ...btnStyle, background: COLORS.green, borderColor: COLORS.green, color: '#fff', flex: 1 }}>
              SUBMIT
            </button>
            <button onClick={() => handleFormSubmit('hold')} style={{ ...btnStyle, background: COLORS.amber, borderColor: COLORS.amber, color: '#fff', flex: 1 }}>
              HOLD
            </button>
          </div>
        </Card>
      );
    }

    // SC-14 — Unidentified Payer (EX-103)
    if (exceptionCode === 'EX-103' || exceptionName.includes('UNIDENTIFIED')) {
      const payerNameOnAch = agent6.payer_name_on_ach || order.payer_name || 'N/A';
      const matchSet = agent6.hitl_packet?.match_set || [];

      return (
        <Card title="❓ UNIDENTIFIED PAYER Resolution Form">
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Payment:</strong> ${parseFloat(order.receipt_amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Payer (as received):</strong> {payerNameOnAch}
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Trace:</strong> {order.ach_trace_number || 'N/A'}
            </div>
          </div>

          {matchSet.length > 0 && (
            <div style={{ marginBottom: 14, padding: '10px', background: COLORS.greenBg, borderRadius: 6, border: `1px solid #86efac` }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: COLORS.green }}>Amount match found:</div>
              {matchSet.map((match, idx) => (
                <div key={idx} style={{ fontSize: 12, color: COLORS.ink }}>
                  {match.invoice_number || 'N/A'} (${parseFloat(match.amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})} exact) — NOT auto-applied, payer not identified
                </div>
              ))}
            </div>
          )}

          <div style={{ marginBottom: 14, padding: '10px', background: COLORS.amberBg, borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: COLORS.amber, fontWeight: 600 }}>
              Problem: payer name has no match in customer_master.alt_payer_names (looks like a third-party AP processor)
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
              Step 1: Which customer does this belong to?
            </label>
            <input
              type="text"
              value={formData.customerSearch}
              onChange={(e) => handleFormChange('customerSearch', e.target.value)}
              placeholder="Search customer name..."
              style={{ width: '100%', padding: '8px', borderRadius: 6, border: `1px solid ${COLORS.line}`, fontSize: 13, marginBottom: 8 }}
            />
            {formData.customerSearch && (
              <div style={{ fontSize: 12, color: COLORS.muted }}>
                Selected: {formData.selectedCustomer || '(select from search results)'}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 14, padding: '10px', background: COLORS.blueSoft, borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Step 2: Add mapping?</div>
            <label style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
              <input
                type="radio"
                name="mapping_action"
                value="add_mapping"
                checked={formData.addMapping === true}
                onChange={() => handleFormChange('addMapping', true)}
                style={{ marginRight: 8, marginTop: 2 }}
              />
              <div>
                <div style={{ fontSize: 12 }}>Add "{payerNameOnAch}" to this customer's alt_payer_names</div>
                <div style={{ fontSize: 10, color: COLORS.red, marginTop: 2 }}>⚠ only if Apex exclusively pays for this one customer — do NOT add if Apex processes for multiple clients</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 8px', cursor: 'pointer', marginBottom: 4 }}>
              <input
                type="radio"
                name="mapping_action"
                value="no_mapping"
                checked={formData.addMapping === false}
                onChange={() => handleFormChange('addMapping', false)}
                style={{ marginRight: 8, marginTop: 2 }}
              />
              <span style={{ fontSize: 12 }}>Do not add — resolve manually each time</span>
            </label>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
              Route: <span style={{ fontWeight: 700, color: COLORS.blue }}>AR_ANALYST</span>
            </label>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
              Notes:
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              placeholder="Add resolution notes..."
              style={{ width: '100%', minHeight: '60px', padding: '8px', borderRadius: 6, border: `1px solid ${COLORS.line}`, fontSize: 13, resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleFormSubmit('submit')} style={{ ...btnStyle, background: COLORS.green, borderColor: COLORS.green, color: '#fff', flex: 1 }}>
              SUBMIT
            </button>
            <button onClick={() => handleFormSubmit('hold')} style={{ ...btnStyle, background: COLORS.amber, borderColor: COLORS.amber, color: '#fff', flex: 1 }}>
              HOLD
            </button>
          </div>
        </Card>
      );
    }

    return null;
  };

  const renderActionDetails = (actionDetails) => {
    if (!actionDetails || !Array.isArray(actionDetails) || actionDetails.length === 0) {
      return <span style={{ color: COLORS.muted }}>-</span>;
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {actionDetails.map((action, idx) => {
          const toneColor = action.tone === "g" ? COLORS.green : action.tone === "a" ? COLORS.amber : action.tone === "r" ? COLORS.red : COLORS.ink;
          return (
            <div key={idx} style={{ color: toneColor, fontSize: 14, lineHeight: 1.4, fontWeight: 500 }}>
              • {action.text || 'No description'}
            </div>
          );
        })}
      </div>
    );
  };

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.exception_type) queryParams.append('exception_type', filters.exception_type);
      if (filters.search) queryParams.append('search', filters.search);

      const url = `${CASH_APP_API_URL}?${queryParams.toString()}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch cash application orders");
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (err) {
      console.error("Error fetching cash application orders:", err);
      setError(err.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Fetch filter options and stats on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const exceptionTypesRes = await fetch(`${CASH_APP_API_URL.replace('/orders', '')}/exception-types`);
        if (exceptionTypesRes.ok) {
          const exceptionTypesData = await exceptionTypesRes.json();
          setExceptionTypes(exceptionTypesData.exception_types || []);
        }
        
        const statusesRes = await fetch(`${CASH_APP_API_URL.replace('/orders', '')}/statuses`);
        if (statusesRes.ok) {
          const statusesData = await statusesRes.json();
          setStatusOptions(statusesData.statuses || []);
        }
        
        const statsRes = await fetch(`${CASH_APP_API_URL.replace('/orders', '')}/stats`);
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  const handleRefresh = () => fetchOrders();
  const handleClearFilters = () => setFilters({ status: "", exception_type: "", search: "" });

  // Fetch order details for sidebar
  const fetchOrderDetails = async (orderId) => {
    setSidebarLoading(true);
    setSidebarError(null);
    try {
      const response = await fetch(`${CASH_APP_API_URL}/${orderId}`);
      if (!response.ok) throw new Error("Failed to fetch order details");
      const data = await response.json();
      console.log('📊 Order details loaded:', {
        orderId,
        hasTimeline: !!data.agent_timeline,
        timelineLength: data.agent_timeline?.length,
        timestamps: data.agent_timeline?.map(step => ({ 
          step: step.step, 
          agent: step.agent, 
          timestamp: step.timestamp 
        }))
      });
      setSelectedOrder(data);
    } catch (err) {
      console.error("Error fetching order details:", err);
      setSidebarError(err.message);
      setSelectedOrder(null);
    } finally {
      setSidebarLoading(false);
    }
  };

  const handleViewDetails = (orderId) => {
    setDrawerOpen(true);
    setActiveTab("details");
    fetchOrderDetails(orderId);
  };

  // Polling function to check for agent updates
  const startPolling = (orderId) => {
    console.log('🔄 Starting polling for order:', orderId);
    setIsPolling(true);
    setPollCount(0);
    
    // Capture initial state to detect changes
    initialStatusRef.current = {
      receipt_status: selectedOrder?.receipt_status,
      post_status: selectedOrder?.post_status,
      agent_timeline_length: selectedOrder?.agent_timeline?.length || 0,
    };
    
    // Poll every 5 seconds indefinitely until completion or manual stop
    const POLL_INTERVAL = 5000;
    
    const checkForCompletion = async () => {
      try {
        const response = await fetch(`${CASH_APP_API_URL}/${orderId}`);
        if (!response.ok) return false;
        
        const data = await response.json();
        
        // Check if agent has completed processing
        const hasNewAgentSteps = (data.agent_timeline?.length || 0) > (initialStatusRef.current?.agent_timeline_length || 0);
        const statusChanged = data.receipt_status !== initialStatusRef.current?.receipt_status || 
                             data.post_status !== initialStatusRef.current?.post_status;
        const hasCompletedStatus = data.receipt_status === 'POSTED' || 
                                   data.receipt_status === 'MATCHED' || 
                                   data.receipt_status === 'CLEARED';
        
        // Update the UI with latest data
        setSelectedOrder(data);
        
        // Determine if processing is complete
        if (hasNewAgentSteps && (statusChanged || hasCompletedStatus)) {
          console.log('✅ Agent processing complete - stopping polling');
          console.log('Status:', { 
            old: initialStatusRef.current?.receipt_status, 
            new: data.receipt_status,
            agentSteps: data.agent_timeline?.length 
          });
          return true;
        }
        
        return false;
      } catch (err) {
        console.error('Error checking completion:', err);
        return false;
      }
    };
    
    pollingIntervalRef.current = setInterval(async () => {
      setPollCount(prevCount => {
        const newCount = prevCount + 1;
        console.log(`📡 Polling attempt ${newCount}`);
        return newCount;
      });
      
      // Check if processing is complete
      const isComplete = await checkForCompletion();
      if (isComplete) {
        stopPolling('complete');
      }
      
      // Also refresh the list
      fetchOrders();
    }, POLL_INTERVAL);
  };

  const stopPolling = (reason = 'manual') => {
    console.log('⏹️ Stopping polling:', reason);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
    setPollCount(0);
    initialStatusRef.current = null;
    
    // Show notification if auto-stopped due to completion
    if (reason === 'complete') {
      console.log('🎉 Updates complete!');
      // You could add a toast notification here if you have one
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Open source document PDF
  const handleOpenSourceDocument = async (orderId) => {
    try {
      const response = await fetch(`${CASH_APP_API_URL}/${orderId}/presigned-url`);
      if (!response.ok) throw new Error('Failed to get document URL');
      const data = await response.json();
      if (data.presigned_url) { 
        window.open(data.presigned_url, '_blank'); 
      } else { 
        console.warn('No document URL available for this order');
        alert('Document not available');
      }
    } catch (err) {
      console.error('Error opening source document:', err);
      alert(`Failed to open document: ${err.message}`);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
      <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif", background: COLORS.page, color: COLORS.ink, fontSize: 14, minHeight: "100vh" }}>
      <div style={{ padding: "20px 22px 60px", maxWidth: 1500, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Cash Application</h2>
            <p style={{ margin: "2px 0 0", color: COLORS.muted, fontSize: 13 }}>Payment matching, variance resolution & cash posting workflow</p>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              onClick={() => window.open("/sop-editor?doc=cash-application", "_blank")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-fiori-border-dark rounded-fiori text-fiori-brand bg-transparent hover:bg-fiori-brand/5 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="w-7 h-7">
                <g transform="translate(1.407 1.407) scale(2.81 2.81)">
                  <path d="M24.731 57.127v-4.129c0-.444-.361-.806-.806-.806H20.05v5.74h3.876c.444 0 .805-.362.805-.805z"/>
                  <path d="M39.841 63.508V53.286c0-.604-.491-1.094-1.094-1.094H35.16v12.41h3.587c.604 0 1.094-.492 1.094-1.094z"/>
                  <path d="M81.979 17.233L66.215 1.469A5.013 5.013 0 0062.67 0H20.59c-2.764 0-5.013 2.249-5.013 5.013v36.168h-4.28c-2.617 0-4.745 2.084-4.745 4.646v25.139c0 2.563 2.128 4.646 4.745 4.646h4.28v9.374c0 2.764 2.249 5.013 5.013 5.013h57.844c2.764 0 5.013-2.249 5.013-5.013v-64.21a5.013 5.013 0 00-1.468-3.543zM18.05 68.603c-1.104 0-2-.896-2-2v-6.67v-9.74c0-1.104.896-2 2-2h5.876c2.65 0 4.806 2.156 4.806 4.806v4.129c0 2.649-2.156 4.806-4.806 4.806H20.05v4.67c0 1.103-.896 1.999-2 1.999zM79.447 84.987c0 .559-.454 1.013-1.013 1.013H20.59c-.559 0-1.013-.454-1.013-1.013v-9.374h44.425c2.616 0 4.745-2.084 4.745-4.646V45.828c0-2.562-2.129-4.646-4.745-4.646H19.578V5.013C19.578 4.454 20.032 4 20.59 4h41.328v12.784c0 2.617 2.129 4.745 4.745 4.745h12.784v63.458zM31.16 66.603v-16.41c0-1.104.896-2 2-2h5.587c2.809 0 5.094 2.285 5.094 5.094v10.222c0 2.81-2.285 5.095-5.094 5.095H33.16c-1.105 0-2-.896-2-2zM54.255 56.397c1.104 0 2 .896 2 2s-.896 2-2 2h-3.686v6.205c0 1.104-.896 2-2 2s-2-.896-2-2v-16.41c0-1.104.896-2 2-2h8.682c1.104 0 2 .896 2 2s-.896 2-2 2h-6.682v4.205h3.686z"/>
                </g>
              </svg>
              SOP
            </button>
            <button onClick={handleRefresh} style={{ ...btnStyle, background: COLORS.blue, borderColor: COLORS.blue, color: "#fff" }} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 18 }}>
          <Kpi label="Orders this month" value={stats.total_orders?.toLocaleString() || '0'} sub={`across ${stats.unique_branches || 0} branches`} />
          <Kpi label="Auto-released" value={stats.auto_released?.toLocaleString() || '0'} sub={`${stats.auto_release_rate || 0}% straight through`} tone="g" />
          <Kpi label="Escalated" value={stats.escalated?.toLocaleString() || '0'} sub="credit review queue" tone="a" />
          <Kpi label="On hold" value={stats.on_hold?.toLocaleString() || '0'} sub="action required" tone="r" />
          <Kpi label="Avg exposure" value={stats.avg_exposure_pct ? `${stats.avg_exposure_pct}%` : 'N/A'} sub="of credit limit" />
        </div>

        {/* Filters */}
        <FilterBar title="Filter Cash Application Orders" onClear={handleClearFilters}>
          <Field label="Status" htmlFor="status">
            <Select id="status" value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
              <option value="">All statuses</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </Select>
          </Field>
          <Field label="Exception Type" htmlFor="exception_type">
            <Select id="exception_type" value={filters.exception_type} onChange={(e) => setFilters((p) => ({ ...p, exception_type: e.target.value }))}>
              <option value="">All exception types</option>
              {exceptionTypes.map(et => (
                <option key={et} value={et}>{et}</option>
              ))}
            </Select>
          </Field>
          <Field label="Search" htmlFor="search">
            <SearchInput id="search" value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} placeholder="Bank txn ID or run ID" />
          </Field>
        </FilterBar>

        {/* Table */}
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10, overflow: "hidden", border: `1px solid ${COLORS.line}` }}>
          <thead>
            <tr>
              {["Bank Txn ID", "Customer", "Invoice Number", "Exception Type", "Received Date", "Last Action", "Amount", "Status", "AI Actions", "Details"].map((h) => (
                <th key={h} style={{ background: COLORS.blue, color: "#fff", fontSize: 14, textTransform: "uppercase", letterSpacing: 0.6, textAlign: "left", padding: "12px 12px", fontWeight: 600 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px 20px' }}><Spinner /> Loading cash application orders...</td></tr>
            ) : error ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.red }}>Error: {error}</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.muted }}><EmptyState title="No orders found" description="No cash application orders match the current filters." /></td></tr>
            ) : (
              orders.map((o, i) => (
              <tr key={o.id || o.runId}>
                <td style={tdStyle(i, orders.length)}>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{o.bankTxnId || o.id || 'N/A'}</div>
                     </td>
                <td style={tdStyle(i, orders.length)}>
                  {o.customer}
                  <div style={{ color: COLORS.muted, fontSize: 12, marginTop: 3 }}>{o.branch !== 'N/A' ? o.branch : ''}</div>
                </td>
                <td style={{...tdStyle(i, orders.length), minWidth: '180px'}}>
                  {o.invoice_numbers && o.invoice_numbers.length > 0 ? (
                    <div style={{ fontSize: 13 }}>
                      {o.invoice_numbers.map((inv, idx) => (
                        <div key={idx} style={{ fontWeight: 500, marginBottom: idx < o.invoice_numbers.length - 1 ? 4 : 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: COLORS.blue, fontSize: 16 }}>•</span>
                          {inv}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: COLORS.muted }}>-</span>
                  )}
                </td>
                <td style={tdStyle(i, orders.length)}>
                  {o.exception_category ? (
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{o.exception_category}</div>
                  ) : (
                    <span style={{ color: COLORS.muted }}>-</span>
                  )}
                </td>
                <td style={tdStyle(i, orders.length)}>
                  {o.received_date ? (
                    <div style={{ fontSize: 13 }}>{new Date(o.received_date).toLocaleDateString()}</div>
                  ) : (
                    <span style={{ color: COLORS.muted }}>-</span>
                  )}
                </td>
                <td style={tdStyle(i, orders.length)}>
                  {o.completed_at ? (
                    <div style={{ fontSize: 13 }}>{new Date(o.completed_at).toLocaleDateString()}</div>
                  ) : (
                    <span style={{ color: COLORS.muted }}>-</span>
                  )}
                </td>
                <td style={tdStyle(i, orders.length)}>{o.value}</td>
                <td style={tdStyle(i, orders.length)}><Pill tone={o.statusPill || o.pill}>{o.status || o.decision}</Pill></td>
                <td style={tdStyle(i, orders.length)}>
                  {o.actions && o.actions.length > 0 ? (
                    <div style={{ background: "#f9fafb", border: `1px solid ${COLORS.line}`, borderRadius: 7, padding: "9px 11px", fontSize: 14, lineHeight: 1.65, maxWidth: 400 }}>
                      <div style={!expandedActions[o.id] ? { overflow: "hidden", maxHeight: "42px" } : {}}>
                        {renderActionDetails(o.actions)}
                      </div>
                      {o.actions.length > 1 && (
                        <button type="button" onClick={() => toggleActions(o.id)}
                          style={{ background: "none", border: "none", color: COLORS.blue, fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 0, marginTop: 4 }}>
                          {expandedActions[o.id] ? "Show less" : "Show more"}
                        </button>
                      )}
                    </div>
                  ) : ( <span style={{ color: COLORS.muted }}>-</span> )}
                </td>
                <td style={tdStyle(i, orders.length)}>
                  <IconButton title="View Details" onClick={() => handleViewDetails(o.runId || o.id)}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12C2.25 12 5.25 6.75 12 6.75C18.75 6.75 21.75 12 21.75 12C21.75 12 18.75 17.25 12 17.25C5.25 17.25 2.25 12 2.25 12Z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  </IconButton>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Scrim */}
      {anyOpen && (
        <div onClick={closeAll} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 40 }} />
      )}

      {/* Order drawer / sidebar */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(660px, 94vw)",
        background: COLORS.page, zIndex: 50,
        transform: drawerOpen ? "translateX(0)" : "translateX(100%)",
        transition: "transform .22s ease", display: "flex", flexDirection: "column" }}>
        <div style={{ background: COLORS.blueDark, color: "#fff", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Cash Application Review</h3>
            {isPolling && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, background: "rgba(255,255,255,0.15)", padding: "4px 10px", borderRadius: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#4ade80", animation: "pulse 2s infinite" }} />
                Updating... ({pollCount})
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isPolling && (
              <button 
                onClick={stopPolling}
                style={{ ...dcloseStyle, width: "auto", padding: "6px 12px", fontSize: 12, fontWeight: 600 }}
              >
                Stop
              </button>
            )}
            <button onClick={closeAll} style={dcloseStyle}>×</button>
          </div>
        </div>
        <div style={{ overflowY: "auto", padding: 0, flex: 1 }}>
          {sidebarLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}><Spinner /> Loading details...</div>
          ) : sidebarError ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.red }}>Error: {sidebarError}</div>
          ) : !selectedOrder ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.muted }}>No order selected</div>
          ) : (
            <>
              {/* Tab Navigation - P2P style */}
              <div style={{ padding: "9px 12px", display: "flex", gap: 6, flexWrap: "wrap", background: "#fff", borderBottom: `1px solid ${COLORS.line}`, position: "sticky", top: 0, zIndex: 10 }}>
                <TabButton active={activeTab === "details"} onClick={() => setActiveTab("details")}>
                  Details
                </TabButton>
                <TabButton active={activeTab === "timeline"} onClick={() => setActiveTab("timeline")}>
                  Agent Timeline
                </TabButton>
                <TabButton 
                  active={activeTab === "processing"} 
                  onClick={() => setActiveTab("processing")}
                 
                >
                  Processing
                </TabButton>
                <TabButton active={activeTab === "source"} onClick={() => setActiveTab("source")}>
                  Source Document 
                </TabButton>
                {/* Only show HITL Form tab if there's an exception and it's not CLEAN */}
                {selectedOrder.exception_name && 
                 selectedOrder.exception_name.toUpperCase() !== 'Clean' && 
                 selectedOrder.exception_code !== 'Clean' && (
                  <TabButton active={activeTab === "hitl"} onClick={() => setActiveTab("hitl")}>
                    HITL Form
                  </TabButton>
                )}
              </div>

              {/* Tab Content */}
              <div style={{ padding: "16px 20px" }}>
                
                {/* TAB 1: Details */}
                {activeTab === "details" && (
                  <>
                    <Card title="Receipt Details">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <KV k="Bank Txn ID" v={selectedOrder.bank_txn_id || 'N/A'} mono />
                  <KV k="Receipt ID" v={selectedOrder.receipt_id || 'N/A'} mono />
                  <KV k="Run ID" v={selectedOrder.run_id} mono />
                  <KV k="Customer ID" v={selectedOrder.customer_id || 'N/A'} mono />
                  <KV k="Customer Name" v={selectedOrder.customer_name || 'N/A'} />
                  <KV k="Receipt Status" v={selectedOrder.receipt_status || 'N/A'} />
                  <KV k="Post Status" v={selectedOrder.post_status || 'N/A'} />
                  <KV k="Disposition" v={selectedOrder.disposition || 'N/A'} />
                  <KV k="Receipt Amount" v={selectedOrder.receipt_amount ? `${parseFloat(selectedOrder.receipt_amount).toLocaleString('en-US', {minimumFractionDigits: 2})} ${selectedOrder.currency || 'USD'}` : 'N/A'} />
                  <KV k="Currency" v={selectedOrder.currency || 'USD'} />
                  <KV k="Value Date" v={selectedOrder.value_date || 'N/A'} />
                  <KV k="Company Code" v={selectedOrder.company_code ? `${selectedOrder.company_code} - ${selectedOrder.company_code_desc || ''}` : 'N/A'} />
                  <KV k="ACH Trace Number" v={selectedOrder.ach_trace_number || 'N/A'} mono />
                  <KV k="SAP Action" v={selectedOrder.sap_action || 'N/A'} />
                  <KV k="Exception Code" v={selectedOrder.exception_code || 'N/A'} />
                  <KV k="Route" v={selectedOrder.route || 'N/A'} />
                  <KV k="SOP ID" v={selectedOrder.sop_id || 'N/A'} mono />
                  <KV k="SOP Version" v={selectedOrder.sop_version || 'N/A'} />
                  <div>
                    <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>SOP</div>
                    <div style={{ marginTop: 2 }}>
                      <SopChip label={`${selectedOrder.sop_id || 'CA-OPS'} · ${selectedOrder.sop_version || 'v1.0'}`} onClick={openSop} />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Exception Name</div>
                    <div style={{ fontSize: 13.5, marginTop: 2, fontWeight: 600 }}>
                      {selectedOrder.exception_name || 'N/A'}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 11, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Pill tone={
                    (selectedOrder.receipt_status === 'POSTED' || selectedOrder.receipt_status === 'MATCHED' || selectedOrder.receipt_status === 'CLEARED') ? 'g' :
                    (selectedOrder.receipt_status === 'PENDING' || selectedOrder.receipt_status === 'REVIEW' || selectedOrder.receipt_status === 'PARTIAL') ? 'a' :
                    (selectedOrder.receipt_status === 'REJECTED' || selectedOrder.receipt_status === 'UNMATCHED' || selectedOrder.receipt_status === 'ERROR') ? 'r' : 'b'
                  }>
                    {selectedOrder.receipt_status || 'Pending'}
                  </Pill>
                  {selectedOrder.disposition && (
                    <span style={{ background: "#ede9fe", color: "#5b21b6", borderRadius: 5, padding: "3px 8px", fontSize: 10.5, fontWeight: 700 }}>
                      {selectedOrder.disposition}
                    </span>
                  )}
                  {selectedOrder.hitl_required && (
                    <span style={{ background: COLORS.amberBg, color: COLORS.amber, borderRadius: 5, padding: "3px 8px", fontSize: 10.5, fontWeight: 700 }}>
                      HITL Required
                    </span>
                  )}
                </div>
              </Card>
                  </>
                )}

                {/* TAB 2: Agent Timeline */}
                {activeTab === "timeline" && (
                  <div style={{ background: "#fff", border: "1px solid #E5E5E5", borderRadius: 6, padding: 12 }}>
                    {selectedOrder.agent_timeline && selectedOrder.agent_timeline.length > 0 ? (
                      selectedOrder.agent_timeline.map((step, index) => (
                        <Step
                          key={step.step || index}
                          n={step.step}
                          status={step.status}
                          desc={step.summary || step.description || `${step.agent}: ${step.status}`}
                          agent={step.agent}
                          time={step.timestamp}
                          isLast={index === selectedOrder.agent_timeline.length - 1}
                          summary={step.details || step.metadata}
                          category={step.category || step.phase}
                          webhook_id={step.webhook_id}
                          tone={step.status === 'COMPLETED' ? undefined : step.status === 'SKIPPED' ? 's' : step.status === 'FAILED' ? 'r' : 'p'}
                        />
                      ))
                    ) : (
                      <div style={{ padding: "26px 12px", textAlign: "center" }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "#1D2D3E" }}>No agent actions recorded yet</p>
                        <small style={{ color: "#556B82" }}>Agent timeline will appear here once processing begins</small>
                      </div>
                    )}
                  </div>
                )}

                {/* TAB 3: Processing */}
                {activeTab === "processing" && (
                  <>
              {/* Matching Evidence Card */}
              {selectedOrder.matching_evidence && (
                <Card title="Matching Evidence">
                  <Row l="Match Type" r={selectedOrder.matching_evidence.match_type || 'N/A'} />
                  <Row l="Matched Tier" r={`Tier ${selectedOrder.matching_evidence.matched_tier || 0}`} />
                  <Row l="Applied Amount" r={selectedOrder.matching_evidence.applied_amount ? `$${parseFloat(selectedOrder.matching_evidence.applied_amount).toLocaleString('en-US', {minimumFractionDigits: 2})}` : '$0.00'} />
                  <Row l="Match Set Total" r={selectedOrder.matching_evidence.match_set_total ? `$${parseFloat(selectedOrder.matching_evidence.match_set_total).toLocaleString('en-US', {minimumFractionDigits: 2})}` : '$0.00'} />
                  <Row 
                    l="Variance" 
                    r={selectedOrder.matching_evidence.variance !== undefined ? `$${parseFloat(selectedOrder.matching_evidence.variance).toLocaleString('en-US', {minimumFractionDigits: 2})}` : '$0.00'}
                    rColor={selectedOrder.matching_evidence.variance === 0 ? COLORS.green : COLORS.red}
                  />
                  <Row l="Variance Direction" r={selectedOrder.matching_evidence.variance_direction || 'NONE'} />
                  {selectedOrder.matching_evidence.tolerance_note && (
                    <div style={{ marginTop: 8, padding: "8px 10px", background: COLORS.blueSoft, borderRadius: 6, fontSize: 12, color: COLORS.blueText }}>
                      <strong>Tolerance Note:</strong> {selectedOrder.matching_evidence.tolerance_note}
                    </div>
                  )}
                </Card>
              )}

              {/* Applications - Invoice Applications */}
              {selectedOrder.applications && selectedOrder.applications.length > 0 && (
                <Card title={`Invoice Applications (${selectedOrder.applications.length})`}>
                  {selectedOrder.applications.map((app, idx) => (
                    <div key={idx} style={{ padding: "10px", background: idx % 2 === 0 ? '#fff' : '#f9fafb', borderRadius: 6, marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{app.invoice_number}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.green }}>
                          ${parseFloat(app.amount_applied || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 2 }}>
                        SAP Doc: {app.sap_doc_number || 'N/A'} · Open Amount: ${parseFloat(app.open_amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 2 }}>
                        Treatment: {app.treatment || 'N/A'} · Variance: ${parseFloat(app.variance || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                      </div>
                      {app.note && (
                        <div style={{ fontSize: 11, color: COLORS.blueText, marginTop: 4, fontStyle: 'italic', background: COLORS.blueSoft, padding: '4px 6px', borderRadius: 4 }}>
                          {app.note}
                        </div>
                      )}
                    </div>
                  ))}
                </Card>
              )}

              {/* Remittance Lines */}
              {selectedOrder.remittance_lines && selectedOrder.remittance_lines.length > 0 && (
                <Card title={`Remittance Lines (${selectedOrder.remittance_lines.length})`}>
                  {selectedOrder.remittance_lines.map((line, idx) => (
                    <div key={idx} style={{ padding: "8px 10px", borderBottom: idx < selectedOrder.remittance_lines.length - 1 ? `1px solid ${COLORS.line}` : 'none' }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{line.ref_value_canonical || line.ref_value_raw}</div>
                          {line.ref_value_raw !== line.ref_value_canonical && (
                            <div style={{ fontSize: 10, color: COLORS.muted }}>Raw: {line.ref_value_raw}</div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>${parseFloat(line.net_paid || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}</div>
                          {line.deduction_amount > 0 && (
                            <div style={{ fontSize: 10, color: COLORS.amber }}>Deduction: ${parseFloat(line.deduction_amount).toLocaleString('en-US', {minimumFractionDigits: 2})}</div>
                          )}
                        </div>
                      </div>
                      {line.deduction_note && (
                        <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 2 }}>{line.deduction_note}</div>
                      )}
                    </div>
                  ))}
                </Card>
              )}
                  </>
                )}

                {/* TAB 4: Source */}
                {activeTab === "source" && (
                  <>
              {/* Source Document */}
              {selectedOrder.source_document && (
                <Card title="Source Document">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.ink }}>{selectedOrder.customer_name?.toUpperCase() || 'N/A'}</div>
                      <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                        Doc: {selectedOrder.source_document.document_id || 'N/A'} · Channel: {selectedOrder.source_document.channel || 'N/A'}
                      </div>
                      
                    </div>
                    {selectedOrder.source_document.filename && (
                      <button
                        onClick={() => handleOpenSourceDocument(selectedOrder.run_id || selectedOrder.receipt_id)}
                        style={{ background: 'transparent', border: 'none', color: COLORS.blue, fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: 16, height: 16 }}>
                          <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V7.621a1.5 1.5 0 00-.44-1.06l-4.12-4.122A1.5 1.5 0 0011.378 2H4.5zm2.25 8.5a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5zm0 3a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z" clipRule="evenodd" />
                        </svg>
                        View PDF
                      </button>
                    )}
                  </div>
                </Card>
              )}

            

              {/* Notifications */}
              {selectedOrder.notifications_sent && selectedOrder.notifications_sent.length > 0 && (
                <Card title="Notifications Sent">
                  {selectedOrder.notifications_sent.map((notif, idx) => (
                    <div key={idx} style={{ marginBottom: 8, padding: "8px 10px", background: "#f9fafb", borderRadius: 6, border: `1px solid ${COLORS.line}` }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                        <Pill tone="b">{notif.channel}</Pill>
                        <span style={{ fontSize: 11, color: COLORS.muted }}>{notif.recipient_role || 'N/A'}</span>
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.ink }}>{notif.subject}</div>
                    </div>
                  ))}
                </Card>
              )}

              {/* Errors */}
              {selectedOrder.errors && selectedOrder.errors.length > 0 && (
                <Card title="Errors">
                  {selectedOrder.errors.map((error, idx) => (
                    <div key={idx} style={{ marginBottom: 8, padding: "8px 10px", background: COLORS.redBg, borderRadius: 6, border: `1px solid #fecaca` }}>
                      <div style={{ fontSize: 12, color: COLORS.red }}>{error}</div>
                    </div>
                  ))}
                </Card>
              )}
                  </>
                )}

                {/* TAB 5: HITL Form */}
                {activeTab === "hitl" && (
                  <>
              {/* Exception Resolution Form - Dynamic based on exception type */}
              {(() => {
                try {
                  return renderExceptionForm(selectedOrder);
                } catch (err) {
                  console.error('Error rendering exception form:', err);
                  return (
                    <div style={{ padding: '20px', textAlign: 'center', color: COLORS.muted }}>
                      <div style={{ fontSize: 14, marginBottom: 8 }}>No HITL form available</div>
                      <div style={{ fontSize: 12 }}>Exception type: {selectedOrder.exception_name || 'Unknown'}</div>
                    </div>
                  );
                }
              })()}
                  </>
                )}

              </div>
            </>
          )}
        </div>

      </div>

      {/* SOP Panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(760px, 96vw)",
        background: "#fff", zIndex: 60,
        transform: sopKey ? "translateX(0)" : "translateX(100%)",
        transition: "transform .22s ease", display: "flex", flexDirection: "column" }}>
        {sopKey && (
          <>
            <div style={{ background: "#312e81", color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{SOPS[sopKey].title}</h3>
                <div style={{ fontSize: 11.5, color: "#c7d2fe", marginTop: 1 }}>{SOPS[sopKey].sub}</div>
              </div>
              <button onClick={closeSop} style={dcloseStyle}>×</button>
            </div>
            <div style={{ overflowY: "auto", padding: "18px 22px", flex: 1 }}>
              <h5 style={{ fontSize: 15, margin: "0 0 4px" }}>Scenario Dispatch</h5>
              <div style={{ fontSize: 11.5, color: COLORS.muted, marginBottom: 14 }}>
                Used by credit_check_agent · primary branch logic
              </div>
              <SopTable rows={SOPS[sopKey].rows} />
            </div>
            <div style={{ borderTop: `1px solid ${COLORS.line}`, background: "#fff", padding: "13px 20px", display: "flex", gap: 9 }}>
              <button onClick={closeSop} style={btnStyle}>Back to decision</button>
              <button style={{ ...btnStyle, background: COLORS.blue, borderColor: COLORS.blue, color: "#fff", marginLeft: "auto" }}>Edit SOP</button>
            </div>
          </>
        )}
      </div>

      </div>
    </>
  );
}





